/** Declarative audio contract: event names, clips and diagnostic snapshot shapes. No engine code. */
import type { PowerUpKind } from '../../shared/power-ups';

/**
 * Chaos cues: one per power-up kind for the player who takes it, `powerUpHit` for a debuff
 * landing on you, `powerUpBlocked` for your shield absorbing one. Spelled out so the tuple
 * keeps literal types; `test/unit/client/audio/sound-pack.test.ts` checks it against
 * `POWER_UP_KINDS`.
 */
export const POWER_UP_SOUND_EVENTS = [
  'powerUpGold',
  'powerUpDouble',
  'powerUpReveal',
  'powerUpShield',
  'powerUpFreeze',
  'powerUpTax',
  'powerUpSpin',
  'powerUpFog',
] as const satisfies readonly PowerUpSoundEvent[];
export type PowerUpSoundEvent = `powerUp${Capitalize<PowerUpKind>}`;

export const SOUND_EVENTS = [
  'select',
  'backtrack',
  'accept',
  'reject',
  'duplicate',
  'countdown',
  'start',
  'end',
  'win',
  'results',
  'navigate',
  ...POWER_UP_SOUND_EVENTS,
  'powerUpHit',
  'powerUpBlocked',
] as const;
export type SoundEvent = (typeof SOUND_EVENTS)[number];

export function powerUpSoundEvent(kind: PowerUpKind): PowerUpSoundEvent {
  return `powerUp${kind.charAt(0).toUpperCase()}${kind.slice(1)}` as PowerUpSoundEvent;
}
export type SoundClip = string | { readonly src: string; readonly volume?: number };
/** Independently toggled, continuously repeating channels; every other pack entry is a one-shot cue. */
export const LOOP_CHANNELS = ['music', 'ambience'] as const;
export type LoopChannel = (typeof LOOP_CHANNELS)[number];
export type SoundPack = Partial<Record<SoundEvent | LoopChannel, SoundClip>>;
export type Haptics = Partial<Record<SoundEvent, readonly number[]>>;

export type AudioContextState = 'suspended' | 'running' | 'closed' | 'interrupted' | 'unknown';

export interface BufferAudioSnapshot {
  state: AudioContextState;
  currentTime: number;
  sampleRate: number;
  running: boolean;
  sources: number;
  buffers: number;
  decoding: boolean;
  decodeQueued: number;
  voices: number;
  pending: number;
  /** Whether a looping source is currently scheduled on the context, per channel. */
  loops: Record<LoopChannel, boolean>;
}

export interface SoundsSnapshot {
  unlocked: boolean;
  suspended: boolean;
  rearmOnUnlock: boolean;
  /** Null before the first audible unlock, or when the browser refused an AudioContext. */
  context: BufferAudioSnapshot | null;
}
